/**
 * Icon wrapper.
 *
 * Wraps lucide-react so consumers don't import from lucide directly.
 * If we later swap to Phosphor, Heroicons, or a custom set, we change
 * only this file.
 *
 * Two components:
 *   - `Icon`         — strict, name: IconName. Use everywhere typesafe.
 *   - `CategoryIcon` — accepts arbitrary string (db-stored category icons
 *                      where the value may be an emoji or unknown legacy
 *                      name); renders the lookup or falls back to text.
 *
 * Adding a new icon:
 *   1. Import from "lucide-react"
 *   2. Add to the ICONS map below with a kebab-case name
 *   3. The IconName union updates automatically
 *
 * The curated registry (vs. re-exporting all of lucide) keeps the bundle
 * small via tree-shaking — only icons we actually use are included.
 */

import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Baby,
  Backpack,
  Banknote,
  Bath,
  Bed,
  Beer,
  Bike,
  Book,
  Briefcase,
  Building,
  Bus,
  Cake,
  Calendar,
  Camera,
  Car,
  Cat,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  CircleDollarSign,
  Coffee,
  Compass,
  Copy,
  CreditCard,
  Crown,
  Dog,
  Droplet,
  Dumbbell,
  Edit2,
  Eye,
  EyeOff,
  File,
  FileText,
  Film,
  Filter,
  Flame,
  Fuel,
  Gamepad2,
  Gift,
  Globe,
  GraduationCap,
  Hammer,
  Headphones,
  Heart,
  HeartPulse,
  Home,
  IceCream,
  Info,
  Lamp,
  Landmark,
  Laptop,
  LayoutGrid,
  Lightbulb,
  Loader2,
  Lock,
  LockOpen,
  LogOut,
  Luggage,
  Mail,
  MapPin,
  Monitor,
  Moon,
  MoreVertical,
  Mountain,
  Music,
  Palmtree,
  Pencil,
  PieChart,
  PiggyBank,
  Pill,
  Pizza,
  Plane,
  Plus,
  Receipt,
  RefreshCw,
  Rows3,
  Scissors,
  Search,
  Settings,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sofa,
  Sparkles,
  Star,
  Stethoscope,
  Sun,
  Tag,
  Tent,
  Ticket,
  TrainFront,
  Trash2,
  TreePine,
  TrendingUp,
  Trophy,
  Tv,
  Umbrella,
  Upload,
  User,
  UserPlus,
  Users,
  Utensils,
  Wallet,
  Wifi,
  Wine,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import type { SVGProps } from "react";

import { cn } from "../../lib/cn";

const ICONS = {
  "alert-circle": AlertCircle,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-right-left": ArrowRightLeft,
  baby: Baby,
  backpack: Backpack,
  banknote: Banknote,
  bath: Bath,
  bed: Bed,
  beer: Beer,
  bike: Bike,
  book: Book,
  briefcase: Briefcase,
  building: Building,
  bus: Bus,
  cake: Cake,
  calendar: Calendar,
  camera: Camera,
  car: Car,
  cat: Cat,
  check: Check,
  "check-circle": CheckCircle2,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "chevrons-up-down": ChevronsUpDown,
  "circle-dollar": CircleDollarSign,
  coffee: Coffee,
  compass: Compass,
  copy: Copy,
  "credit-card": CreditCard,
  crown: Crown,
  dog: Dog,
  droplet: Droplet,
  dumbbell: Dumbbell,
  edit: Edit2,
  eye: Eye,
  "eye-off": EyeOff,
  file: File,
  "file-text": FileText,
  film: Film,
  filter: Filter,
  flame: Flame,
  fuel: Fuel,
  gamepad: Gamepad2,
  gift: Gift,
  globe: Globe,
  "graduation-cap": GraduationCap,
  hammer: Hammer,
  headphones: Headphones,
  heart: Heart,
  "heart-pulse": HeartPulse,
  home: Home,
  "ice-cream": IceCream,
  info: Info,
  lamp: Lamp,
  landmark: Landmark,
  laptop: Laptop,
  "layout-grid": LayoutGrid,
  lightbulb: Lightbulb,
  loader: Loader2,
  lock: Lock,
  unlock: LockOpen,
  "log-out": LogOut,
  luggage: Luggage,
  mail: Mail,
  "map-pin": MapPin,
  monitor: Monitor,
  moon: Moon,
  "more-vertical": MoreVertical,
  mountain: Mountain,
  music: Music,
  "palm-tree": Palmtree,
  pencil: Pencil,
  "pie-chart": PieChart,
  "piggy-bank": PiggyBank,
  pill: Pill,
  pizza: Pizza,
  plane: Plane,
  plus: Plus,
  receipt: Receipt,
  "refresh-cw": RefreshCw,
  "rows-3": Rows3,
  scissors: Scissors,
  search: Search,
  settings: Settings,
  ship: Ship,
  shirt: Shirt,
  "shopping-bag": ShoppingBag,
  "shopping-cart": ShoppingCart,
  smartphone: Smartphone,
  sofa: Sofa,
  sparkles: Sparkles,
  star: Star,
  stethoscope: Stethoscope,
  sun: Sun,
  tag: Tag,
  tent: Tent,
  ticket: Ticket,
  train: TrainFront,
  trash: Trash2,
  tree: TreePine,
  "trending-up": TrendingUp,
  trophy: Trophy,
  tv: Tv,
  umbrella: Umbrella,
  upload: Upload,
  user: User,
  "user-plus": UserPlus,
  users: Users,
  utensils: Utensils,
  wallet: Wallet,
  wifi: Wifi,
  wine: Wine,
  wrench: Wrench,
  x: X,
  zap: Zap,
} as const;

export type IconName = keyof typeof ICONS;

export function isIconName(value: string): value is IconName {
  return value in ICONS;
}

const SIZE_CLASS = {
  xs: "h-3 w-3",
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
  xl: "h-8 w-8",
} as const;

type IconSize = keyof typeof SIZE_CLASS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: IconSize;
  /** Set to true if the icon is purely decorative (default: true). */
  decorative?: boolean;
}

export function Icon({ name, size = "sm", className, decorative = true, ...props }: IconProps) {
  const Component = ICONS[name];
  return (
    <Component
      className={cn(SIZE_CLASS[size], "shrink-0", className)}
      aria-hidden={decorative ? true : undefined}
      strokeWidth={1.75}
      {...props}
    />
  );
}

export interface CategoryIconProps {
  /** Free-form string from category data — an IconName, an emoji, or any text. */
  name: string | null | undefined;
  size?: IconSize;
  className?: string;
  /** Set to true if the icon is purely decorative (default: true). */
  decorative?: boolean;
}

/**
 * Renders a category icon stored as a free-form string. Looks up the value in
 * the icon registry; if not found, renders the raw string (so emoji like 📦
 * or unknown legacy names display as text instead of crashing React).
 */
export function CategoryIcon({
  name,
  size = "sm",
  className,
  decorative = true,
}: CategoryIconProps) {
  if (name && isIconName(name)) {
    return <Icon name={name} size={size} className={className} decorative={decorative} />;
  }
  return (
    <span
      className={cn(
        SIZE_CLASS[size],
        "inline-flex shrink-0 items-center justify-center",
        className,
      )}
      aria-hidden={decorative ? true : undefined}
    >
      {name}
    </span>
  );
}
